import { FC, ReactNode } from 'react';
import { LocaleData } from '../../../models/locale/locale-data';
import { ResolvedVariantCopy } from '../../../utils/landing/getVariantCopy';
import { ClockIcon } from '../../../assets/icons/ClockIcon';
import heroImage from '../../../assets/images/hero-lp1-hybrid.jpg';
import { V2SmallPrint } from './v2';

/**
 * Sections for the LP1 "hybrid" (`phone-only-v2`, Sept 2026): the control
 * page's 10-year headline, V1's approved copy and phone numbers, and a
 * mobile-first layout (black banner, grey offer block, V1's pale-blue 60-day
 * tile, stacked ticks, repeated phone button). Mockups and review packs:
 * claude-notes/lp-hybrid-lp1.
 *
 * Kept apart from the V1 sections (v2.tsx) so neither design can drift into
 * the other; only the legal small print is shared, as the footer is "as per V1".
 * Styles are the `.hyb-*` classes in assets/styles.css.
 */

/** A phone button's label; honours the explicit line break in the IT copy. */
export const HybridButtonLabel: FC<{ text: string }> = ({ text }) => (
  <span className="hyb-btn-label">{text}</span>
);

export const HybridFrame: FC<{
  data: LocaleData;
  copy: ResolvedVariantCopy;
  /** CTA 1: heading + primary phone button. */
  cta: ReactNode;
  /** CTA 2: heading + repeated phone button, after the benefits. */
  repeatCta: ReactNode;
}> = ({ data, copy, cta, repeatCta }) => {
  const [headlineLarge, headlineSmall] = copy.offerHeadline ?? [];
  return (
    <div className="grid grid-cols-12">
      <div className="col-span-12 hyb">
        <article className="hyb-bar">
          <p>{copy.banner}</p>
        </article>
        <div className="hyb-hero">
          <img src={heroImage} alt="" />
        </div>
        <section className="hyb-offer">
          <h1>
            <span className="hyb-offer-large">{headlineLarge}</span>
            <span className="hyb-offer-small">{headlineSmall}</span>
          </h1>
        </section>
        <div className="hyb-main">
          <div className="hyb-deadline">
            <ClockIcon />
            <span>{copy.sixtyDayHeader}</span>
          </div>
          {cta}
          <section className="hyb-benefits-block">
            <p className="hyb-benefits-head">{copy.benefitsHeader}</p>
            <ul className="hyb-benefits">
              {(copy.benefitTicks ?? []).map((tick, index) => (
                <li key={index}>
                  <span className="hyb-tick">✓</span>
                  <span>{tick}</span>
                </li>
              ))}
            </ul>
          </section>
          <p className="hyb-hours">{copy.phoneSubCopy}</p>
          {repeatCta}
        </div>
      </div>
      <V2SmallPrint data={data} copy={copy} />
    </div>
  );
};
